<template>
	<div class="fight-tutorial">
		<ScrollFade>
			<template #scrollContent>
				<div class="fight-tutorial__scroll-content">
					<div
						v-for="(section, index) in list"
						:key="section.title"
						class="fight-tutorial__division"
					>
						<div class="fight-tutorial__division__subheader">
							{{ translate(section.title) }}
						</div>

						<!-- Level 1 List -->
						<ul>
							<li v-for="item in section.items" :key="item.text">
								<span>{{ translate(item.text) }}</span>

								<!-- Level 2 List -->
								<ul
									v-if="item.children && item.children.length"
								>
									<li
										v-for="subItem in item.children"
										:key="subItem.text"
									>
										<span>{{
											translate(subItem.text)
										}}</span>

										<!-- Level 3 List -->
										<ul
											v-if="
												subItem.children &&
												subItem.children.length
											"
										>
											<li
												v-for="deepItem in subItem.children"
												:key="deepItem.text"
											>
												<span>{{
													translate(deepItem.text)
												}}</span>
											</li>
										</ul>
									</li>
								</ul>
							</li>
						</ul>
						<HorizontalDecorDivider
							with-sides
							v-if="index < list.length - 1"
						/>
					</div>
					<AppTextButton
						class="fight-intro__popup__btn"
						:type="StatusTypes.PRIMARY"
						:text="translate('button.continue')"
						@click="nextPage"
					/>
					<div class="fight-tutorial__pagination">2/2</div>
				</div>
			</template>
		</ScrollFade>
	</div>
</template>

<script lang="ts" setup>
import AppTextButton from "@/components/AppTextButton.vue";
import ScrollFade from "@/components/ScrollFade.vue";
import HorizontalDecorDivider from "@/components/SVG/HorizontalDecorDivider.vue";
import { usePopupStore } from "@/store/usePopupStore";
import { StatusTypes } from "@/types/ui";
import { translate } from "@/utils/translate";
import { markRaw } from "vue";

const popupStore = markRaw(usePopupStore());
// object with texts for nested list items
type ListItem = {
	title: string;
	items: Array<{
		text: string;
		children: Array<{
			text: string;
			children?: Array<{ text: string }>;
		}>;
	}>;
};
const list: Array<ListItem> = [
	{
		title: "ui.preparation",
		items: [
			{
				text: "ui.scouting",
				children: [
					{
						text: "ui.scouting_info",
					},
				],
			},
			{
				text: "ui.unit_deployment",
				children: [
					{
						text: "ui.unit_deployment_info",
					},
				],
			},
			{
				text: "ui.battle_start",
				children: [
					{
						text: "ui.battle_start_info",
					},
				],
			},
		],
	},
	{
		title: "ui.course_of_battle",
		items: [
			{
				text: "ui.two_attack_types",
				children: [
					{
						text: "ui.regular_attack",
						children: [
							{
								text: "ui.regular_attack_info",
							},
						],
					},
					{
						text: "ui.direct_attack",
						children: [
							{
								text: "ui.direct_attack_info",
							},
						],
					},
				],
			},
			{
				text: "ui.range",
				children: [
					{
						text: "ui.range_info",
					},
				],
			},
			{
				text: "ui.enemy_attacks",
				children: [
					{
						text: "ui.enemy_attacks_info",
					},
					{
						text: "ui.enemy_attacks_info_2",
					},
				],
			},
			{
				text: "ui.inactivity",
				children: [
					{
						text: "ui.inactivity_info",
					},
				],
			},
		],
	},
	{
		title: "ui.important_to_know",
		items: [
			{
				text: "ui.bonuses",
				children: [
					{
						text: "ui.bonuses_info",
					},
				],
			},
			{
				text: "ui.unit_management",
				children: [
					{
						text: "ui.unit_management_info",
					},
				],
			},
			{
				text: "ui.supplies",
				children: [
					{
						text: "ui.supplies_info",
					},
				],
			},
		],
	},
	{
		title: "ui.battle_end",
		items: [
			{
				text: "ui.victory",
				children: [
					{
						text: "ui.victory_info",
					},
				],
			},
			{
				text: "ui.defeat",
				children: [
					{
						text: "ui.defeat_info",
					},
				],
			},
		],
	},
];
const nextPage = (): void => {
	popupStore.hidePopup();
};
</script>

<style lang="scss" scoped>
.fight-tutorial {
	gap: 0.5rem;
	border-width: 0.125rem;
	border-style: solid;
	border-right: none;
	border-bottom: none;
	border-left: none;
	border-image-source: linear-gradient(to top, #bd9032, #f0d386);
	border-image-slice: 1;
	width: 100%;
	height: 100%;
	@include flex-col-center;
	position: relative;
	background: linear-gradient(
		0deg,
		rgba(171, 136, 73, 1) 0%,
		rgba(242, 220, 181, 1) 100%
	);

	&::before {
		position: absolute;
		top: 0;
		left: 0;
		opacity: 1;
		mix-blend-mode: multiply;
		background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
			repeat center;
		background-size: 40%;
		width: 100%;
		height: 100%;
		content: "";
	}

	&__scroll-content {
		padding: 0.5rem 1rem;
		width: 100%;
		height: 100%;
		@include flex-col-center;
		gap: 0.5rem;
	}
	&__division {
		width: 100%;
		@include flex-col-start;
		gap: 0.5rem;
		color: rgb(55, 38, 21);
		font-size: 0.8125rem;

		&__subheader {
			width: 100%;
			@include flex-row-start;
			color: rgb(55, 38, 21);
			font-weight: 800;
			font-size: 1.1875rem;
		}

		ul {
			display: flex;
			flex-direction: column;
			justify-content: flex-start;
			align-items: flex-start;
			padding-left: 1.125rem;
			width: 100%;
			li {
				text-align: left;
			}
		}
	}
	:deep(.text-button) {
		margin-top: 1rem;
	}
	&__pagination {
		color: rgb(55, 38, 21);
		font-size: 0.9063rem;
	}
}
</style>
